import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, downloadFile } from "@/lib/api";
import { Printer, Search } from "lucide-react";

const HEADERS = ["Sr. No.", "Beneficiary ID", "Student Name", "Course", "Month", "Working Days", "Present Days", "Absent Days", "Attendance %", "Signature"];

const FIELDS = [
  ["विद्यार्थ्यांचे नाव", "Student's Name", (b) => b.full_name],
  ["महाविद्यालयाचे नाव", "College Name", (b) => b.institution],
  ["महाविद्यालय कोणत्या विद्यापिठाशी संलग्न आहे", "Affiliated University", () => ""],
  ["अभ्यासक्रमाचे नाव", "Course Name", (b) => b.course],
  ["अभ्यासक्रमाचा कालावधी", "Course Duration", () => ""],
  ["विद्यार्थ्यांच्या जातीचा प्रवर्ग", "Caste Category", (b) => b.category],
  ["अभ्यासक्रमाचे वर्ष (प्रथम/द्वितीय/तृतीय/चतुर्थ)", "Year of Course", (b) => b.class_year],
  ["मागील सत्र परिक्षेतील गुणांची टक्केवारी", "Last Semester %", (b) => b.last_year_percent],
  ["विद्यार्थ्यांचा मोबाईल क्रमांक", "Mobile Number", (b) => b.mobile],
  ["विद्यार्थी दिव्यांग आहे का?", "Is student disabled?", () => ""],
  ["अर्जाचा प्रकार NEW / RENEW", "Application Type", (b) => b.application_type],
];

export default function AttendanceSampleForm() {
  const [view, setView] = useState("list");
  const [rows, setRows] = useState([]);
  const [taluka, setTaluka] = useState("");
  const [talukas, setTalukas] = useState([]);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [student, setStudent] = useState(null);

  const load = (t) => api.get("/attendance/sample-form", { params: { taluka: t || undefined } })
    .then(({ data }) => setRows(data.rows)).catch((e) => toast.error(errMsg(e)));

  useEffect(() => { load(""); api.get("/beneficiaries/options").then(({ data }) => setTalukas(data.talukas)).catch(() => {}); }, []);

  const search = async () => {
    try {
      const { data } = await api.get("/beneficiaries", { params: { search: query, page_size: 25 } });
      setResults(data.items);
      if (data.items.length === 0) toast.error("No beneficiary found");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-4" data-testid="attendance-form-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-serif text-3xl font-semibold text-[#0f2545] sm:text-4xl">Attendance Forms</h1>
          <p className="text-sm text-slate-500">उपस्थिती नमुना · Swadhar Scheme, District Latur</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {view === "list" ? (
            <>
              <select data-testid="att-taluka-filter" value={taluka} onChange={(e) => { setTaluka(e.target.value); load(e.target.value); }} className="rounded-md border border-slate-300 px-2 py-2 text-sm">
                <option value="">All Talukas</option>{talukas.map((t) => <option key={t}>{t}</option>)}
              </select>
              <button data-testid="att-export-xlsx" onClick={() => downloadFile("/attendance/sample-form/export/xlsx", "attendance_sample_form.xlsx", { taluka: taluka || undefined })} className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">Excel</button>
              <button data-testid="att-export-pdf" onClick={() => downloadFile("/attendance/sample-form/export/pdf", "attendance_sample_form.pdf", { taluka: taluka || undefined })} className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">PDF</button>
            </>
          ) : null}
          <button data-testid="att-print" onClick={() => window.print()} className="flex items-center gap-1.5 rounded-md bg-[#0f2545] px-3 py-2 text-sm text-white"><Printer size={15} /> Print</button>
        </div>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-slate-200">
        {[["list", "List Format (Excel/PDF)"], ["official", "परिशिष्ट-१ Official Form"]].map(([k, l]) => (
          <button key={k} data-testid={`att-view-${k}`} onClick={() => setView(k)}
            className={`px-3 py-2 text-sm ${view === k ? "border-b-2 border-[#0f2545] font-semibold text-[#0f2545]" : "text-slate-600"}`}>{l}</button>
        ))}
      </div>

      {view === "list" && (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="min-w-[900px] w-full text-sm">
            <thead className="bg-slate-100 text-left text-xs uppercase text-slate-600"><tr>{HEADERS.map((h) => <th key={h} className="p-3">{h}</th>)}</tr></thead>
            <tbody data-testid="att-rows">
              {rows.map((r) => (
                <tr key={r.beneficiary_id} className="border-t border-slate-100">
                  <td className="p-3">{r.sr_no}</td><td className="p-3 font-mono text-xs">{r.beneficiary_id}</td>
                  <td className="p-3">{r.student_name}</td><td className="p-3">{r.course}</td><td className="p-3">{r.month}</td>
                  <td className="p-3">{r.working_days}</td><td className="p-3">{r.present_days}</td><td className="p-3">{r.absent_days}</td>
                  <td className="p-3">{r.percentage}%</td><td className="p-3 text-slate-300">__________</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {view === "official" && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2 rounded-xl border border-slate-200 bg-white p-4 print:hidden">
            <input data-testid="att-official-search" value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()}
              placeholder="Search student by name, ID or application number…" className="min-w-[260px] flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm" />
            <button data-testid="att-official-search-btn" onClick={search} className="flex items-center gap-1.5 rounded-md bg-[#0f2545] px-4 py-2 text-sm text-white"><Search size={15} /> Search</button>
            {results.length > 0 && (
              <select data-testid="att-official-select" onChange={(e) => setStudent(results.find((r) => r.beneficiary_id === e.target.value) || null)} className="rounded-md border border-slate-300 px-2 py-2 text-sm">
                <option value="">Select student…</option>
                {results.map((r) => <option key={r.beneficiary_id} value={r.beneficiary_id}>{r.beneficiary_id} — {r.full_name}</option>)}
              </select>
            )}
          </div>

          <div className="mx-auto max-w-3xl rounded-xl border border-slate-300 bg-white p-6 text-sm" data-testid="official-form">
            <div className="text-right text-xs">परिशिष्ट-१</div>
            <div className="mb-4 text-center">
              <div className="font-serif text-lg font-semibold text-[#0f2545]">भारतरत्न डॉ. बाबासाहेब आंबेडकर स्वाधार योजना</div>
              <div className="text-sm">विद्यार्थी उपस्थिती — शैक्षणिक वर्ष 2025 - 2026</div>
              <div className="text-xs text-slate-500">समाज कल्याण कार्यालय, जिल्हा लातूर</div>
            </div>
            <table className="w-full border border-slate-400 text-sm">
              <tbody>
                {FIELDS.map(([mr, en, get], i) => (
                  <tr key={mr} className="border-b border-slate-300">
                    <td className="w-8 border-r border-slate-300 p-2 text-center">{i + 1}</td>
                    <td className="border-r border-slate-300 p-2">{mr}<div className="text-[11px] text-slate-400">{en}</div></td>
                    <td className="p-2 font-medium">{student ? (get(student) || "") : ""}</td>
                  </tr>
                ))}
                <tr className="border-b border-slate-300">
                  <td className="border-r border-slate-300 p-2 text-center">12</td>
                  <td className="border-r border-slate-300 p-2">प्रथम सहामाही हजेरी<div className="text-[11px] text-slate-400">First half-year attendance</div></td>
                  <td className="p-2">{student?.attendance_status === "Submitted" ? `${student?.attendance?.percentage ?? ""}%` : ""}</td>
                </tr>
                <tr>
                  <td className="border-r border-slate-300 p-2 text-center"></td>
                  <td className="border-r border-slate-300 p-2">व्दितीय सहामाही हजेरी<div className="text-[11px] text-slate-400">Second half-year attendance</div></td>
                  <td className="p-2"></td>
                </tr>
              </tbody>
            </table>
            <p className="mt-4 text-xs leading-relaxed text-slate-700">
              वरील माहिती महाविद्यालयाचे अभिलेख्यानुसार बरोबर असून त्या आधारे विद्यार्थ्यास स्वाधार योजनेचा लाभ मिळण्यासाठी शिफारस करण्यात येत आहे.
              याबाबतची माहिती खोटी आढळल्यास त्यास प्रशासकीय प्रमुख या नात्याने मी जबाबदार राहील.
            </p>
            <div className="mt-8 flex items-end justify-between text-xs">
              <div>दिनांक :  /  / 2026</div>
              <div className="text-center">
                <div className="mb-6">स्वाक्षरी</div>
                <div>महाविद्यालयाचे नाव व शिक्का</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
